// neo_cogym — 메인 셸 (W6 + W8)
// 세션 게이트(로그인) → 코스/차시 카탈로그 로드 → 콘텐츠를 ContentPane 에 연결(entitlement 게이팅)
// + 블록 에디터(scratch-gui iframe + shim) | 콘텐츠 분할화면, 코드위즈 연결상태, 6종 반응형.

import { useCallback, useEffect, useState } from "react";
import { EditorHost } from "./ui/EditorHost";
import { ContentPane } from "./ui/ContentPane";
import { ConnectionStatus } from "./ui/ConnectionStatus";
import { Login } from "./ui/Login";
import { CodewizDevice } from "./device/codewiz";
import type { ShimHandle } from "./compat/electronApiShim";
import type { ContentAsset } from "./content/types";
import { me, logout, type SessionUser } from "./api/session";
import { getCourses, getCourse, toContentAsset, type CourseSummary, type LessonDetail } from "./api/catalog";
import { checkout, testComplete } from "./api/payment";
import { SeatAdmin } from "./ui/SeatAdmin";
import "./styles.css";

const ADMIN_ROLES = ["owner", "admin", "teacher"];

const webSerial = typeof navigator !== "undefined" && "serial" in navigator;

export function App() {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [booting, setBooting] = useState(true);
  const [status, setStatus] = useState("disconnected");
  const [shim, setShim] = useState<ShimHandle | null>(null);
  const [tab, setTab] = useState<"editor" | "content">("editor");

  const [courses, setCourses] = useState<CourseSummary[]>([]);
  const [lessons, setLessons] = useState<LessonDetail[]>([]);
  const [asset, setAsset] = useState<ContentAsset | null>(null);
  const [locked, setLocked] = useState(false);
  const [courseId, setCourseId] = useState<string | null>(null);
  const [product, setProduct] = useState<{ id: string; amountMinor: number; currency: string } | null>(null);
  const [view, setView] = useState<"learn" | "seats">("learn");
  const [buying, setBuying] = useState(false);

  // 부팅: 기존 세션 복원
  useEffect(() => {
    me().then((u) => {
      setUser(u);
      setBooting(false);
    });
  }, []);

  // 로그인 후 코스 목록 로드
  useEffect(() => {
    if (!user) return;
    getCourses().then((r) => setCourses(r.courses)).catch(() => setCourses([]));
  }, [user]);

  // 코스 선택 → 차시/콘텐츠 로드(entitlement 게이팅)
  const openCourse = useCallback(async (id: string) => {
    const detail = await getCourse(id);
    setCourseId(id);
    setLessons(detail.lessons);
    setLocked(!detail.entitled);
    setProduct(detail.product);
    setAsset(null);
    for (const l of detail.lessons) {
      const c = l.contents[0];
      if (c) {
        const a = toContentAsset(l.id, c);
        if (a) {
          setAsset(a);
          break;
        }
      }
    }
  }, []);

  // 구매(테스트): checkout → 테스트 결제완료 → 코스 재로드(권리 반영)
  const onBuy = useCallback(async () => {
    if (!product || !courseId) return;
    setBuying(true);
    try {
      const { orderId } = await checkout(product.id);
      await testComplete(orderId);
      await openCourse(courseId);
    } catch {
      /* 결제 오류 — 잠금 유지 */
    } finally {
      setBuying(false);
    }
  }, [product, courseId, openCourse]);

  const onConnect = useCallback(() => {
    try {
      shim?.device.connect(CodewizDevice.recommend());
    } catch {
      setStatus("disconnected");
    }
  }, [shim]);
  const onDisconnect = useCallback(() => void shim?.device.disconnect(), [shim]);

  if (booting) return <div className="boot">불러오는 중…</div>;
  if (!user) return <Login onLogin={setUser} />;

  return (
    <div className="shell">
      <header className="topbar">
        <div className="brand">neo_cogym</div>
        <ConnectionStatus
          status={status}
          webSerial={webSerial}
          ready={!!shim}
          onConnect={onConnect}
          onDisconnect={onDisconnect}
        />
        <select
          className="course-select"
          defaultValue=""
          onChange={(e) => e.target.value && openCourse(e.target.value)}
          aria-label="코스 선택"
        >
          <option value="" disabled>코스 선택…</option>
          {courses.map((c) => (
            <option key={c.id} value={c.id}>{c.title}</option>
          ))}
        </select>
        <nav className="tab-switch" aria-label="패널 전환">
          <button className={tab === "editor" ? "on" : ""} onClick={() => setTab("editor")}>에디터</button>
          <button className={tab === "content" ? "on" : ""} onClick={() => setTab("content")}>교재</button>
        </nav>
        {ADMIN_ROLES.includes(user.role) && (
          <button
            className="admin-toggle"
            onClick={() => setView((v) => (v === "learn" ? "seats" : "learn"))}
          >
            {view === "learn" ? "좌석관리" : "학습화면"}
          </button>
        )}
        <button className="logout" onClick={() => logout().then(() => setUser(null))}>로그아웃</button>
      </header>

      {view === "seats" ? (
        <main className="single">
          <SeatAdmin />
        </main>
      ) : (
      <main className="split">
        <section className={`pane editor-pane ${tab === "editor" ? "show" : ""}`}>
          <EditorHost onDeviceStatus={setStatus} onShimReady={setShim} getAuthToken={() => null} />
        </section>
        <section className={`pane content-pane-wrap ${tab === "content" ? "show" : ""}`}>
          {locked ? (
            <div className="locked">
              <h3>🔒 수강권이 필요합니다</h3>
              <p>이 코스의 콘텐츠를 보려면 구매 또는 좌석 배정이 필요합니다.</p>
              {product && (
                <button className="buy-btn" onClick={onBuy} disabled={buying}>
                  {buying
                    ? "결제 중…"
                    : `구매 (${(product.amountMinor / (product.currency === "KRW" ? 1 : 100)).toLocaleString()} ${product.currency}, 테스트)`}
                </button>
              )}
            </div>
          ) : (
            <>
              {lessons.length > 0 && (
                <ul className="lesson-list">
                  {lessons.map((l) =>
                    l.contents.map((c) => {
                      const a = toContentAsset(l.id, c);
                      return (
                        <li key={c.id}>
                          <button disabled={!a} onClick={() => a && setAsset(a)}>
                            {l.position}. {c.title} <span className="badge">{c.type}</span>
                          </button>
                        </li>
                      );
                    }),
                  )}
                </ul>
              )}
              <ContentPane asset={asset} />
            </>
          )}
        </section>
      </main>
      )}
    </div>
  );
}
